import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { SonicFacilityData } from "../../../common/classes/SonicFacility";
import NoResults from "../../../common/NoResults";
import ActionBtn from "../../../common/management/ActionBtn";
import Modal from "../../../common/management/Modal";
import LoadingSpinner from "../../../common/LoadingSpinner";

import { PageVisibility } from "../../../utils/PageVisibility";
import { genStatusText } from "../../../utils/GeneralUtils";
import { CategoryState } from "../../../utils/CategoryState";
import { RequestState } from "../../../utils/RequestState";

import styles from "../../management/db-list/DBList.module.scss";

type Props = {
  facilities: SonicFacilityData[];
  requestState: RequestState;
  handleDelete: (id: string) => void;
};

const FacilityDeletionRequestList: React.FC<Props> = ({
  facilities,
  requestState,
  handleDelete,
}) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [itemIndex, setItemIndex] = useState<number | undefined>(undefined);

  if (facilities.length === 0) return <NoResults />;

  return (
    <div className={styles["f-dbList"]}>
      <div
        className={`${styles["f-dbList__category"]} ${styles["head"]} ${styles["facilityDelete"]}`}
      >
        <div>施設名</div>
        <div>都道府県</div>
        <div>公開状況</div>
        <div>会員登録</div>
        <div>アクション</div>
      </div>
      {facilities.map((f, i) => (
        <FacilityListItem
          key={f.id}
          id={f.id}
          name={f.name}
          prefecture={f.prefecture}
          status={f.status}
          ownerName={f.owner?.name}
          onClick={() => {
            setModalOpen(true);
            setItemIndex(i);
          }}
        />
      ))}
      {modalOpen && itemIndex !== undefined && (
        <div className={styles["f-databaseDetail__modal-overlay"]}>
          <Modal
            closeModal={() => setModalOpen(false)}
            handler={() => handleDelete(facilities[itemIndex].id)}
            categoryState={CategoryState.Delete}
            requestState={requestState}
            headText="施設削除"
            buttonText={
              requestState !== RequestState.Success &&
              requestState !== RequestState.Failed ? (
                <>
                  <FontAwesomeIcon icon={["fal", "trash-alt"]} />
                  <span>削除する</span>
                </>
              ) : undefined
            }
          >
            {requestState === RequestState.Success ? (
              <>施設を削除しました</>
            ) : requestState === RequestState.Failed ? (
              <>施設削除に失敗しました。</>
            ) : requestState === RequestState.Doing ? (
              <LoadingSpinner space={1} />
            ) : (
              <>
                施設を削除すると復元できません。
                <br />
                下記施設を削除してよろしいですか？
                <p className={styles["f-databaseDetail__modal-name"]}>
                  {facilities[itemIndex].name}
                </p>
              </>
            )}
          </Modal>
        </div>
      )}
    </div>
  );
};

export default FacilityDeletionRequestList;

type ListItemProps = {
  id: string;
  name: string;
  prefecture?: string;
  status: PageVisibility;
  ownerName?: string;
  onClick: () => void;
};

const FacilityListItem: React.FC<ListItemProps> = ({
  id,
  name,
  prefecture,
  status,
  ownerName,
  onClick,
}) => {
  const statusStyle = () => {
    switch (status) {
      case PageVisibility.PubliclyVisible:
        return `${styles["status"]} ${styles["open"]}`;
      case PageVisibility.AwaitingInspection:
        return `${styles["status"]} ${styles["pending"]}`;
      default:
        return `${styles["status"]} ${styles["close"]}`;
    }
  };

  return (
    <div
      className={`${styles["f-dbList__category"]} ${styles["body"]} ${styles["facilityDelete"]}`}
    >
      <div>{name}</div>
      <div>{prefecture ?? "未設定"}</div>
      <div>
        <span className={statusStyle()}>{genStatusText(status)}</span>
      </div>
      <div>{ownerName ?? "未設定"}</div>
      <div>
        <ActionBtn
          label="詳細"
          icon={["fal", "edit"]}
          link={`/admin/facility/${id}`}
        />
        <ActionBtn
          label="削除"
          icon={["fal", "trash-alt"]}
          isDelete={true}
          onClick={() => onClick()}
        />
      </div>
    </div>
  );
};
